import { useState } from "react";

import { Navigate, useParams, useSearchParams } from "react-router";

import { SEOHead } from "@/components/helpers/seo/SEOHead";
import { isWorkspaceAdmin } from "@/components/helpers/workspace/permissions";
import { Box } from "@/components/layout/flexbox";
import { Nav } from "@/components/nav/Nav";
import { Authenticate } from "@/components/pages_logged_in/Authenticate";
import { ScenarioHeader } from "@/components/pages_logged_in/roleplays/details/ScenarioHeader";
import { ScenarioAssignmentContent } from "@/components/pages_logged_in/roleplays/ScenarioAssignmentContent";
import { PermissionError } from "@/components/pages_public/403";
import { NotFoundInAppPage } from "@/components/pages_public/404/NotFound";
import { SIDE_NAV_WIDTH } from "@/css/constants";
import { useRoleplayQuery, useScenarioQuery } from "@/graphql";
import { ListScenario } from "@/types/roleplays";

import { EditScenario } from "./edit/EditScenario";

function ScenarioAssignmentContainer() {
  const { scenarioSlug = "", roleplaySlug = "" } = useParams();
  const [searchParams] = useSearchParams();
  const isBareLayout = searchParams.get("layout") === "bare";

  const isAdmin = isWorkspaceAdmin();
  if (!isAdmin) {
    return <PermissionError />;
  }

  const { data, loading } = useScenarioQuery({
    // 'network-only' to avoid cache issues when update permissions
    fetchPolicy: "network-only",
    variables: {
      scenarioSlug,
    },
    skip: !scenarioSlug,
  });
  const [editScenarioTab, setEditScenarioTab] = useState<
    "basic" | "character" | "scenario" | null
  >(null);
  const [editScenario, setEditScenario] = useState<ListScenario | null>(null);
  const scenario = data?.scenario;

  const { data: roleplayData } = useRoleplayQuery({
    variables: {
      slug: roleplaySlug,
    },
    skip: !roleplaySlug,
  });

  const roleplay = roleplayData?.roleplay;
  const isScenarioBelongToRoleplay =
    roleplay?.scenarios?.some((s) => s?.slug === scenarioSlug) || false;
  const isValidUrl = !roleplaySlug || (roleplay && isScenarioBelongToRoleplay);

  if (!loading && !scenario) {
    return <NotFoundInAppPage />;
  }

  if (!loading && !isValidUrl) {
    return <Navigate to={`/scenarios/${scenarioSlug}`} />;
  }

  return (
    <>
      <SEOHead title={`${scenario?.name || ""} - Scenario Details - Exec`} />
      {!isBareLayout && <Nav pageLoading={loading} />}
      <Box ml={isBareLayout ? 0 : [0, 0, SIDE_NAV_WIDTH]}>
        <ScenarioHeader
          page={"scenarioDetail"}
          roleplay={roleplay}
          scenario={scenario}
          loading={loading}
          onEditScenario={() => {
            setEditScenarioTab("basic");
            setEditScenario(scenario as ListScenario);
          }}
        />
        {scenario && (
          <ScenarioAssignmentContent
            pageType="scenarioDetail"
            scenarioId={scenario.id}
          />
        )}
        {editScenario && editScenarioTab && (
          <EditScenario
            key={`${editScenario.id}-${editScenarioTab}`}
            initialTab={editScenarioTab}
            scenario={editScenario}
            onClose={() => setEditScenarioTab(null)}
          />
        )}
      </Box>
    </>
  );
}

export function ScenarioAssignmentPage() {
  const [searchParams] = useSearchParams();
  const isBareLayout = searchParams.get("layout") === "bare";

  return (
    <Authenticate
      maxWidth="100%"
      layout={isBareLayout ? "compact" : "full"}
      navHeight={isBareLayout ? 0 : undefined}
    >
      <ScenarioAssignmentContainer />
    </Authenticate>
  );
}
